import {useMe} from '@/haligator-factory';
import {Box, Flex, HStack, Image, Text, VStack} from '@chakra-ui/react';
import {HaliRenderOne} from '@synako/haligator';
import {useTranslation} from 'react-i18next';
import {RightSingleArrow} from '@/components/svg/RightSingleArrowIcon';
import {SwitchContact} from 'api-types/ffconnect';
import {PartieGaucheConnexion} from './PartieGaucheConnexion';
import {LOGO_FF_COULEUR_URL} from '@/domains/mediatheque/logoFFCouleurUrl';
import {useEffect} from 'react';
import {imageOptimisee} from '../mediatheque/optimisation';
import {BookOpenIcon} from '@/components/svg/BookOpenIcon';
import {UserIcon} from '@/components/svg/UserIcon';
import {LocationIcon} from '@/components/svg/LocationIcon';

export const SelectClientView = () => {
  const {t, i18n} = useTranslation();

  useEffect(() => {
    if (i18n.language !== 'fr') {
      i18n.changeLanguage('fr');
    }
  }, [i18n]);

  return (
    <HaliRenderOne
      nav={useMe().follow('switch')}
      render={({data: {items: choices}}) => {
        // Séparer les clients FFR_R-100 ou RBR_R-100 des autres
        const clientsGrandCompte = choices.filter(
          (item) =>
            item.id.startsWith('FFR_R-100') || item.id.startsWith('RBR_R-100'),
        );
        const autresClients = choices.filter(
          (item) =>
            !item.id.startsWith('FFR_R-100') &&
            !item.id.startsWith('RBR_R-100'),
        );
        const afficherCatalogueGrandCompte = clientsGrandCompte.length > 0;

        return (
          <Flex
            minHeight='100vh'
            direction={{base: 'column', md: 'row'}}
            bg='white'
          >
            {/* Partie gauche - cachée sur mobile */}
            <Box
              position='fixed'
              top={0}
              left={0}
              maxWidth='800px'
              width='50vw'
              height='100vh'
              display={{base: 'none', md: 'block'}}
            >
              <PartieGaucheConnexion />
            </Box>

            {/* Partie droite */}
            <Box
              marginLeft={{base: 0, md: '50%'}}
              width={{base: '100%', md: '50%'}}
              minHeight='100vh'
              overflowY='auto'
            >
              <Flex
                width='100%'
                height='100%'
                justify='center'
                align='center'
                px={{base: 0, md: 24}}
                py={8}
              >
                <VStack
                  width='100%'
                  maxWidth='700px'
                  minWidth={{base: 'none', md: '500px'}}
                  spacing={8}
                  bg='#317FD51A'
                  borderRadius='16px'
                  px={{base: 6, md: 8}}
                  py='2em'
                >
                  {/* Logo */}
                  <Image
                    src={imageOptimisee(LOGO_FF_COULEUR_URL, 300)}
                    alt={t('global.alt_logo_france_frais')}
                    marginX='auto'
                    w='120px'
                    mt={{base: '30px', md: '0'}}
                  />

                  <VStack
                    width='100%'
                    spacing={4}
                    align='stretch'
                  >
                    <Text
                      color='blue.main'
                      fontWeight='bold'
                      fontSize='xl'
                      textAlign='center'
                    >
                      {t('select_client.greeting')}
                    </Text>
                    <Text
                      color='blue.main'
                      textAlign='center'
                      mb='0.5em'
                    >
                      {t('select_client.selectionnez_compte')}
                    </Text>
                    {afficherCatalogueGrandCompte && (
                      <SelectCatalogueGrandCompte
                        premierClientGrandCompte={clientsGrandCompte[0]}
                      />
                    )}
                    <Box
                      overflow='auto'
                      w='100%'
                      bgColor='white'
                      borderRadius='16px'
                    >
                      {autresClients.map((item, index) => (
                        <SelectClient
                          key={item.id}
                          item={item}
                          index={index}
                          count={autresClients.length}
                        />
                      ))}
                    </Box>
                  </VStack>
                </VStack>
              </Flex>
            </Box>
          </Flex>
        );
      }}
    />
  );
};

const SelectClient: React.FC<{
  item: SwitchContact;
  index: number;
  count: number;
}> = ({item}) => {
  const redirect_to = '/authn/switch-contact';
  const params = new URLSearchParams();
  params.append('contact_id', item.id);
  const href = `${redirect_to}?${params.toString()}`;
  const urlLogo = item.societe_commerciale?.url_logo;
  return (
    <a href={href}>
      <HStack
        justifyContent='space-between'
        borderBottom='0.5px solid #cfcfcf'
      >
        <Box p='1em 1em'>
          <HStack
            gap='1em'
            pl='0.2em'
            py='0.5em'
          >
            {/* Logo societe commerciale */}
            <Image
              src={imageOptimisee(urlLogo, 64)}
              alt={item.societe_commerciale?.nom_commercial}
              htmlWidth='32'
              htmlHeight='32'
              maxW='32px'
              objectFit='contain'
              margin='-1'
            />
            {item.societe_commerciale?.nom_commercial && (
              <Text
                fontSize='xs'
                color='gray.dark'
              >
                {item.societe_commerciale.nom_commercial}
              </Text>
            )}
          </HStack>
          <Text
            fontWeight='bold'
            fontSize='sm'
            my='0.5em'
            color='blue.main'
          >
            {item.client?.raison_sociale}
          </Text>

          <HStack
            gap='6'
            color='gray.dark'
          >
            <HStack>
              <UserIcon
                color='gray.dark'
                width='4'
                height='4'
              />
              <Text fontSize='xs'>
                {item.contact.prenom} {item.contact.nom}
              </Text>
            </HStack>
            <HStack>
              <LocationIcon
                color='gray.dark'
                width='4'
                height='4'
              />
              <Text fontSize='xs'>
                {item.client?.adresse_de_facturation.ville}
              </Text>
            </HStack>
          </HStack>
        </Box>
        <Box pr='1em'>
          <RightSingleArrow />
        </Box>
      </HStack>
    </a>
  );
};

const SelectCatalogueGrandCompte: React.FC<{
  premierClientGrandCompte: SwitchContact;
}> = ({premierClientGrandCompte}) => {
  const redirect_to = '/authn/switch-contact';
  const params = new URLSearchParams();
  params.append('contact_id', premierClientGrandCompte.id);
  const href = `${redirect_to}?${params.toString()}`;
  const {t} = useTranslation();

  return (
    <a href={href}>
      <HStack
        overflow='auto'
        w='100%'
        bgColor='white'
        borderRadius='14px'
        p='1em'
      >
        <Box
          bg='blue.main_transparency_10'
          borderRadius='8px'
        >
          <BookOpenIcon
            color='blue.main'
            width='12'
            height='12'
          />
        </Box>
        <Text
          mr='auto'
          fontWeight='bold'
          color='blue.main'
        >
          {t('select_client.mon_catalogue_france_frais')}
        </Text>
        <RightSingleArrow />
      </HStack>
    </a>
  );
};
